// React Query mutation hooks for Counseling Manual (PRD 11 §3.4, §3.5, §3.7).

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { counselingManualApi } from '../api/counseling-manual.adapter';
import type {
  ConsultationManualDetail,
  CreateManualInput,
  UpdateManualInput,
  UploadResult,
} from '../types/counseling-manual.types';
import { counselingManualQueryKeys } from './use-manuals';

// SHCT-454 — upload an image-block file; returns the storage URL to store on the
// block (no cache invalidation — the URL is written into local form state).
export function useUploadImage() {
  return useMutation<UploadResult, Error, File>({
    mutationFn: (file) => counselingManualApi.uploadImage(file),
  });
}

// PRD §3.4 — create (임시저장 / 게시). BE assigns the CID. List + detail caches
// are invalidated/seeded so the table and any re-open reflect the new row.
export function useCreateManual() {
  const qc = useQueryClient();
  return useMutation<ConsultationManualDetail, Error, CreateManualInput>({
    mutationFn: (input) => counselingManualApi.create(input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: counselingManualQueryKeys.lists() });
      qc.setQueryData(counselingManualQueryKeys.detail(created.cid), created);
    },
  });
}

// PRD §3.5 — edit an existing manual.
export function useUpdateManual() {
  const qc = useQueryClient();
  return useMutation<
    ConsultationManualDetail,
    Error,
    { cid: string; input: UpdateManualInput }
  >({
    mutationFn: ({ cid, input }) => counselingManualApi.update(cid, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: counselingManualQueryKeys.lists() });
      qc.setQueryData(counselingManualQueryKeys.detail(updated.cid), updated);
    },
  });
}

// PRD §3.7 — hard delete (Root-only, CONTENT_LIB:DELETE). The list query is
// invalidated on success so the table refreshes immediately. Errors propagate
// to the caller for toast handling.
export function useDeleteManual() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (cid) => counselingManualApi.remove(cid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: counselingManualQueryKeys.lists() });
    },
  });
}
